import { FC, PropsWithChildren, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuthContext } from "@/context/AuthContext.tsx";
import TripLayout from "@/layouts/TripLayout/TripLayout.tsx";
import Routes from "@/router/Routes.ts";
import { isLegacyAvoidValue } from "@/utils/dna/avoidance.ts";
import { missingTranslationToReadable } from "@/utils/missingTranslationToReadable.ts";
import TravelDnaStep, {
  type TravelDnaStep as TravelDnaStepValue,
} from "@/_constants/TravelDnaStep.ts";

type ProfileCardProps = PropsWithChildren<{
  title: string;
  onEdit: () => void;
  editLabel: string;
}>;

const ProfileCard: FC<ProfileCardProps> = ({
  title,
  onEdit,
  editLabel,
  children,
}) => (
  <section className="rounded-xl border border-primary bg-white px-4 py-3 md:px-5 md:py-4">
    <div className="flex items-center justify-between gap-4 border-b border-primary/40 pb-2.5">
      <h2 className="font-feature text-2xl font-semibold text-base-content">
        {title}
      </h2>
      <button
        type="button"
        onClick={onEdit}
        className="font-sans text-sm font-semibold text-primary hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        {editLabel}
      </button>
    </div>
    <dl>{children}</dl>
  </section>
);

type ProfileRowProps = {
  label: string;
  value: ReactNode;
};

const ProfileRow: FC<ProfileRowProps> = ({ label, value }) => (
  <div className="grid gap-1 border-b border-black/20 py-2.5 last:border-b-0 sm:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] sm:items-start sm:gap-5">
    <dt className="font-sans text-sm font-medium text-[#80691F] md:text-base">
      {label}
    </dt>
    <dd className="min-w-0 font-sans text-sm text-base-content sm:text-right md:text-base">
      {value}
    </dd>
  </div>
);

type ChipListProps = {
  items: string[];
  emptyText: string;
  firm?: boolean;
};

const ChipList: FC<ChipListProps> = ({ items, emptyText, firm = false }) => {
  if (items.length === 0) {
    return <span className="text-text-secondary">{emptyText}</span>;
  }

  return (
    <span className="flex flex-wrap gap-2 sm:justify-end">
      {items.map((item, index) => (
        <span
          key={`${item}-${index}`}
          className={`rounded-full border px-3 py-1 text-xs ${
            firm
              ? "border-primary bg-primary/15 text-[#80691F]"
              : "border-primary bg-journi-theme-light text-[#80691F]"
          }`}
        >
          {item}
        </span>
      ))}
    </span>
  );
};

const ProfilePage: FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const dna = user?.dna?.data;
  const critical = user?.dna?.critical_requirements;

  const noneSelected = t("dnaConfirmationPage.noneSelected", {
    defaultValue: "None selected",
  });
  const nothingAdded = t("dnaConfirmationPage.nothingAdded", {
    defaultValue: "Nothing added",
  });
  const editLabel = t("button.edit");

  const translateFirstExisting = (keys: string[], fallback: string): string => {
    const key = keys.find((candidate) => i18n.exists(candidate));
    return key ? String(t(key)) : fallback;
  };

  const translateDnaValue = (section: string, value: string): string =>
    translateFirstExisting(
      [`travelDnaPage.${section}.${value}`],
      missingTranslationToReadable(value),
    );

  const translateDetail = (
    section: string,
    category: string,
    value: string,
  ): string =>
    translateFirstExisting(
      [
        `travelDnaPage.${section}Details.${category}.${value}`,
        `travelDnaPage.${section}.${category}.${value}`,
        `travelDnaPage.${section}.${value}`,
      ],
      missingTranslationToReadable(value),
    );

  const pace = dna?.how_do_you_like_to_travel
    ? translateDnaValue(
        "howDoYouLikeToTravel",
        dna.how_do_you_like_to_travel,
      )
    : noneSelected;
  const accommodation = dna?.what_type_of_place_do_you_prefer
    ? translateDnaValue(
        "whatTypeOfPlaceDoYouPrefer",
        dna.what_type_of_place_do_you_prefer,
      )
    : noneSelected;
  const homeItems =
    dna?.where_do_you_feel_most_at_home?.map((item) =>
      translateDnaValue("whereDoYouFeelMostAtHome", item),
    ) ?? [];
  const dining = dna?.your_approach_to_food_when_you_travel
    ? translateDnaValue(
        "yourApproachToFoodWhenYouTravel",
        dna.your_approach_to_food_when_you_travel,
      )
    : noneSelected;
  const foodItems = [
    ...(critical?.food_preferences?.map((item) =>
      translateFirstExisting(
        [
          `travelDnaPage.foodPreferences.${item}`,
          `travelDnaPage.yourFoodPreferences.options.${item}`,
          `travelDnaPage.yourFoodPreferences.${item}`,
        ],
        missingTranslationToReadable(item),
      ),
    ) ?? []),
    ...(critical?.food_preferences_custom
      ? [critical.food_preferences_custom]
      : []),
  ];
  const explore = dna?.how_do_you_like_to_explore
    ? translateDnaValue(
        "howDoYouLikeToExplore",
        dna.how_do_you_like_to_explore,
      )
    : noneSelected;
  const plan =
    dna?.how_much_do_you_like_a_plan != null
      ? String(
          t(
            `travelDnaPage.yourPlans.captions.${dna.how_much_do_you_like_a_plan}`,
          ),
        )
      : noneSelected;
  const spending =
    dna?.spending_preference != null
      ? String(
          t(
            `travelDnaPage.yourSpending.captions.${dna.spending_preference}`,
          ),
        )
      : noneSelected;
  const splurgeItems =
    dna?.what_do_you_splurge_on?.map((item) =>
      translateFirstExisting(
        [
          `travelDnaPage.whatDoYouSplurgeOn.${item}`,
          `travelDnaPage.yourSplurges.options.${item}`,
          `travelDnaPage.yourSplurges.${item}`,
        ],
        missingTranslationToReadable(item),
      ),
    ) ?? [];
  const interestItems = [
    ...(dna?.what_makes_great_trip_for_you?.flatMap((category) => {
      const details = dna.what_makes_great_trip_for_you_details?.[category] ?? [];
      return details.length > 0
        ? details.map((detail) =>
            translateDetail("whatMakesGreatTripForYou", category, detail),
          )
        : [translateDnaValue("whatMakesGreatTripForYou", category)];
    }) ?? []),
    ...(dna?.what_makes_great_trip_for_you_custom
      ? [dna.what_makes_great_trip_for_you_custom]
      : []),
  ];
  const avoidItems = [
    ...(dna?.what_should_avoid_when_planning?.flatMap((category) => {
      const details = dna.what_should_avoid_when_planning_details?.[category] ?? [];
      return details.length > 0
        ? details.map((detail) =>
            translateDetail("whatShouldAvoidWhenPlanning", category, detail),
          )
        : isLegacyAvoidValue(category)
          ? [translateDnaValue("whatShouldAvoidWhenPlanning", category)]
          : [];
    }) ?? []),
    ...(dna?.what_should_avoid_when_planning_custom
      ? [dna.what_should_avoid_when_planning_custom]
      : []),
  ];
  const accessibilityItems = [
    ...(critical?.accessibility_requirements?.map((item) =>
      translateFirstExisting(
        [
          `travelDnaPage.accessibilityRequirements.${item}`,
          `travelDnaPage.accessAndSupport.${item}`,
        ],
        missingTranslationToReadable(item),
      ),
    ) ?? []),
    ...(critical?.accessibility_requirements_custom
      ? [critical.accessibility_requirements_custom]
      : []),
  ];

  const headlineInterest = interestItems[0] ?? null;
  const headlineExplore =
    dna?.how_do_you_like_to_explore && explore !== noneSelected
      ? explore
      : null;

  const handleEdit = (
    startStep: TravelDnaStepValue,
    endStep: TravelDnaStepValue = startStep,
  ) => {
    navigate(Routes.DNA_EDIT, {
      state: {
        dnaEdit: {
          startStep,
          endStep,
          returnTo: location.pathname,
        },
      },
    });
  };

  return (
    <div className="min-h-[100dvh] w-full overflow-y-auto bg-journi-theme-light px-5 pb-12 pt-4 text-base-content md:px-8 lg:px-12">
      <div className="mx-auto w-full max-w-[1100px]">
        <header className="flex items-center gap-3 border-b border-primary/40 pb-3">
          <TripLayout.OpenMenuButton
            color="ghost"
            className="border-2 border-primary text-primary"
          />
          <button
            type="button"
            onClick={() => navigate(Routes.DASHBOARD)}
            className="font-feature text-2xl font-semibold tracking-wide"
          >
            JOURNI
          </button>
        </header>

        <section className="mt-5 rounded-[30px] bg-black px-6 py-6 text-journi-theme-light md:px-8 md:py-7">
          <p className="font-sans text-sm font-bold uppercase text-primary">
            {t("settingsPage.your_travel_dna", {
              defaultValue: "Your Travel DNA",
            })}
          </p>
          <h1 className="mt-2 max-w-[900px] font-feature text-2xl font-semibold italic leading-snug md:text-3xl">
            {headlineInterest || headlineExplore ? (
              <>
                You travel for{" "}
                <span className="text-primary">
                  {[headlineInterest, headlineExplore]
                    .filter(Boolean)
                    .join(" and ")}
                </span>
                .
              </>
            ) : (
              t("settingsPage.no_dna", {
                defaultValue: "Your Travel DNA will appear here.",
              })
            )}
          </h1>
          <div className="mt-4 border-t-2 border-primary pt-3">
            <p className="font-sans text-sm font-semibold text-primary">
              {t("settingsPage.in_short", { defaultValue: "In short:" })}
            </p>
            <p className="mt-0.5 font-sans font-semibold">
              {[pace, dining, explore]
                .filter((value) => value !== noneSelected)
                .join(" · ") || noneSelected}
            </p>
          </div>
        </section>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() =>
              handleEdit(TravelDnaStep.YOUR_PACE, TravelDnaStep.ANYTHING_ELSE)
            }
            className="font-feature text-lg italic text-[#80691F] hover:underline"
          >
            {t("settingsPage.update_dna", {
              defaultValue: "Update my Travel DNA →",
            })}
          </button>
        </div>

        <h2 className="mt-2 border-b border-primary/40 pb-2 font-sans text-xl text-text-secondary">
          {t("settingsPage.preferences_in_detail", {
            defaultValue: "Your preferences, in detail",
          })}
        </h2>

        <div className="mt-4 flex flex-col gap-4">
          <ProfileCard
            title={t("dnaConfirmationPage.howYouTravel", {
              defaultValue: "How you travel",
            })}
            editLabel={editLabel}
            onEdit={() =>
              handleEdit(TravelDnaStep.YOUR_PACE, TravelDnaStep.YOUR_SANCTUARY)
            }
          >
            <ProfileRow label={t("dnaConfirmationPage.pace", { defaultValue: "Pace" })} value={pace} />
            <ProfileRow label={t("dnaConfirmationPage.accommodationStyle", { defaultValue: "Where you stay" })} value={accommodation} />
            <ProfileRow label={t("dnaConfirmationPage.feelsMostLikeHome", { defaultValue: "Feels most like home" })} value={<ChipList items={homeItems} emptyText={noneSelected} />} />
          </ProfileCard>

          <ProfileCard
            title={t("dnaConfirmationPage.atTheTable", { defaultValue: "At the table" })}
            editLabel={editLabel}
            onEdit={() => handleEdit(TravelDnaStep.YOUR_TABLE, TravelDnaStep.YOUR_FOOD_PREFERENCES)}
          >
            <ProfileRow label={t("dnaConfirmationPage.whenEatingOut", { defaultValue: "When eating out" })} value={dining} />
            <ProfileRow label={t("dnaConfirmationPage.foodPreferences", { defaultValue: "Preferences" })} value={<ChipList items={foodItems} emptyText={noneSelected} firm />} />
            {critical?.food_allergy_details ? <ProfileRow label={t("dnaConfirmationPage.allergies", { defaultValue: "Allergies" })} value={critical.food_allergy_details} /> : null}
          </ProfileCard>

          <ProfileCard
            title={t("dnaConfirmationPage.howYouExplore", { defaultValue: "How you explore" })}
            editLabel={editLabel}
            onEdit={() => handleEdit(TravelDnaStep.YOUR_WORLD)}
          >
            <ProfileRow label={t("dnaConfirmationPage.onceThere", { defaultValue: "Once there" })} value={explore} />
          </ProfileCard>

          <ProfileCard
            title={t("dnaConfirmationPage.planningAndMoney", { defaultValue: "Planning & money" })}
            editLabel={editLabel}
            onEdit={() => handleEdit(TravelDnaStep.YOUR_PLANS, TravelDnaStep.YOUR_SPLURGES)}
          >
            <ProfileRow label={t("dnaConfirmationPage.howMuchYouPlan", { defaultValue: "How much you plan" })} value={plan} />
            <ProfileRow label={t("dnaConfirmationPage.spendingStyle", { defaultValue: "Spending style" })} value={spending} />
            <ProfileRow label={t("dnaConfirmationPage.worthTheSplurge", { defaultValue: "Worth the splurge" })} value={<ChipList items={splurgeItems} emptyText={noneSelected} />} />
          </ProfileCard>

          <ProfileCard
            title={t("dnaConfirmationPage.whatYouLove", { defaultValue: "What you love" })}
            editLabel={editLabel}
            onEdit={() => handleEdit(TravelDnaStep.YOUR_TRIP)}
          >
            <ProfileRow label={t("dnaConfirmationPage.interests", { defaultValue: "Interests" })} value={<ChipList items={interestItems} emptyText={noneSelected} />} />
          </ProfileCard>

          <ProfileCard
            title={t("dnaConfirmationPage.whatToAvoid", { defaultValue: "Always avoid" })}
            editLabel={editLabel}
            onEdit={() => handleEdit(TravelDnaStep.YOUR_DEFAULTS)}
          >
            <ProfileRow label={t("dnaConfirmationPage.codyWillAvoid", { defaultValue: "Cody will be mindful of" })} value={<ChipList items={avoidItems} emptyText={noneSelected} />} />
          </ProfileCard>

          <ProfileCard
            title={t("dnaConfirmationPage.accessAndSupport", { defaultValue: "Access & support" })}
            editLabel={editLabel}
            onEdit={() => handleEdit(TravelDnaStep.YOUR_ACCESSIBILITY)}
          >
            <ProfileRow label={t("dnaConfirmationPage.supportRequirements", { defaultValue: "Support requirements" })} value={<ChipList items={accessibilityItems} emptyText={noneSelected} firm />} />
          </ProfileCard>

          <ProfileCard
            title={t("dnaConfirmationPage.theHumanBit", { defaultValue: "The human bit" })}
            editLabel={editLabel}
            onEdit={() => handleEdit(TravelDnaStep.ANYTHING_ELSE)}
          >
            <ProfileRow label={t("dnaConfirmationPage.whatCodyShouldKnow", { defaultValue: "What Cody should know" })} value={critical?.anything_else_journi_should_know || nothingAdded} />
          </ProfileCard>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;